/**
 * w-projectId
 * w-projectId-feature
 * w-projectId-feature-featureId
 *
 *
 * r-projectId
 * r-projectId-feature
 * r-projectId-feature-featureId
 *
 *
 *
 *
 *
 *
 */
export default class RoleUtils {
    projectId;
    contractId;
    dbPrefix;
    constructor(projectId, contractId = null, dbPrefix = null) {
        this.projectId = projectId;
        this.contractId = contractId;
        this.dbPrefix = dbPrefix;
    }
    get anyoneReadWrite() {
        return [`p-${this.projectId}`, `p-${this.projectId}-basic`]; // Databases such as feed, tags, files that can be written by all users.
    }
    get anyoneReadAdminWrite() {
        return [`p-${this.projectId}`, `p-${this.projectId}-basic`, `p-${this.projectId}-write`]; // Databases such as feed, tags, files that can be written by all users.
    }
    get peopleRoles() {
        return [...this.anyoneReadAdminWrite, `p-${this.projectId}-people-write`];
    }
    get featureBaseRoles() {
        return [`p-${this.projectId}`, `p-${this.projectId}-write`]; // must not include -basic
    }
    get fileRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-files`, `p-${this.projectId}-files-write`];
    }
    get drawingRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-drawings`, `p-${this.projectId}-drawings-write`];
    }
    get specificationRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-specifications`, `p-${this.projectId}-specifications-write`];
    }
    get scheduleRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-schedules`, `p-${this.projectId}-schedules-write`];
    }
    get scheduleWorklogsRoles() {
        return [...this.scheduleRoles, `p-${this.projectId}-worklogs`, `p-${this.projectId}-worklogs-write`];
    }
    /**
     * With these logs, the admins can't create new types, even a user with schedule write roles cant do it.
     */
    get hoursTypesRoles() {
        return [`p-${this.projectId}-type`, `p-${this.projectId}-type-write`];
    }
    /**
     * with these roles, the project admins and those with schedule permissions can't add logs
     */
    get hoursLogRoles() {
        return [...this.anyoneReadAdminWrite, `p-${this.projectId}-log`, `p-${this.projectId}-log-write`];
    }
    get digestSignerRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-sign-digest`];
    }
    static isDigestSigner(roles, projectId) {
        return new RoleUtils(projectId).digestSignerRoles.some(r => roles?.includes(r));
    }
    get scheduleFilterRoles() {
        return [`p-${this.projectId}`, `p-${this.projectId}-schedules`]; // no write restriction on schedule filters
    }
    get rfiRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-rfis`, `p-${this.projectId}-rfis-write`];
    }
    get submittalRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-submittals`, `p-${this.projectId}-submittals-write`];
    }
    get contractRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-contracts`, `p-${this.projectId}-contracts-write`];
    }
    get subcontractRoles() {
        return [...this.featureBaseRoles, `p-${this.projectId}-subcontracts`, `p-${this.projectId}-subcontracts-write`];
    }
    get contractIdRoles() {
        if (!this.contractId) {
            throw new Error(`contractId must be set`);
        }
        if (!this.dbPrefix) {
            throw new Error(`dbPrefix must be set`);
        }
        return [
            ...this.featureBaseRoles,
            `p-${this.projectId}-${this.dbPrefix}-${this.contractId}`,
            `p-${this.projectId}-${this.dbPrefix}-${this.contractId}-write`,
        ];
    }
    get contractIdUniqueRoles() {
        if (!this.contractId || !this.dbPrefix) {
            throw new Error(`contractId must be set`);
        }
        return [
            `p-${this.projectId}-${this.dbPrefix}-${this.contractId}`,
            `p-${this.projectId}-${this.dbPrefix}-${this.contractId}-write`,
        ];
    }
    get contractIdWriteRoles() {
        if (!this.contractId || !this.dbPrefix) {
            throw new Error(`contractId must be set`);
        }
        return [`p-${this.projectId}-${this.dbPrefix}-${this.contractId}-write`];
    }
    get contractIdReadOnlyRoles() {
        if (!this.contractId || !this.dbPrefix) {
            throw new Error(`contractId must be set`);
        }
        return [`p-${this.projectId}-${this.dbPrefix}-${this.contractId}`];
    }
    static getBasicRole(projectId) {
        return `p-${projectId}-basic`;
    }
    static getRoleGroups(projectId) {
        return [
            {
                icon: 'business-outline',
                title: 'Project Wide Access',
                roles: [
                    { name: 'Can View Entire Project', role: `p-${projectId}` },
                    { name: 'Can make changes anywhere in the project', role: `p-${projectId}-write` },
                ],
            },
            {
                icon: 'people',
                title: 'Manage People',
                roles: [{ name: 'Can invite users to the project and update access', role: `p-${projectId}-people-write` }],
            },
            {
                icon: 'calendar',
                title: 'Schedule',
                roles: [
                    { name: 'Can view the Schedule', role: `p-${projectId}-schedules` },
                    { name: 'Can upload and edit schedules', role: `p-${projectId}-schedules-write` },
                ],
            },
            {
                icon: 'document',
                title: 'Files',
                roles: [{ name: 'Can upload Files', role: `p-${projectId}-files-write` }],
            },
            {
                icon: 'help-circle',
                title: 'RFIs',
                roles: [
                    { name: 'Can view RFIs', role: `p-${projectId}-rfis` },
                    { name: 'Can create and edit RFIS', role: `p-${projectId}-rfis-write` },
                ],
            },
            {
                icon: 'document-text',
                title: 'Submittals',
                roles: [
                    { name: 'Can view Submittals', role: `p-${projectId}-submittals` },
                    { name: 'Can create and edit Submittals', role: `p-${projectId}-submittals-write` },
                ],
            },
            {
                icon: 'document',
                title: 'Contracts',
                roles: [
                    { name: 'Can view Contracts', role: `p-${projectId}-contracts` },
                    { name: 'Can create and edit Contracts', role: `p-${projectId}-contracts-write` },
                ],
            },
            {
                icon: 'document',
                title: 'Subcontracts',
                roles: [
                    { name: 'Can view Subcontracts', role: `p-${projectId}-subcontracts` },
                    { name: 'Can create and edit Subcontracts', role: `p-${projectId}-subcontracts-write` },
                ],
            },
            {
                icon: 'settings',
                title: 'Schedule Settings',
                roles: [
                    { name: 'Can view Schedule Settings', role: `p-${projectId}-type` },
                    { name: 'Can create and edit schedule settings', role: `p-${projectId}-type-write` },
                ],
            },
            {
                icon: 'document-text',
                title: 'Estimates and Daily Logs',
                roles: [
                    { name: 'Can view Estimates and Daily Logs', role: `p-${projectId}-log` },
                    { name: 'Can create and edit estimates and daily logs', role: `p-${projectId}-log-write` },
                ],
            },
        ];
    }
    static getBasicRoles(projectId) {
        return [`p-${projectId}`, `p-${projectId}-write`, `p-${projectId}-basic`];
    }
    static canReadWriteBasic(roles, projectId) {
        return this.getBasicRoles(projectId).some(role => roles.includes(role));
    }
    static canWrite(roles, projectId, feature, featureId) {
        if (projectId && feature && featureId && this.hasFeatureIdWriteRole(roles, projectId, feature, featureId)) {
            return true;
        }
        else if (projectId && feature && this.hasFeatureWriteRole(roles, projectId, feature)) {
            return true;
        }
        else if (projectId && this.hasPojectWriteRole(roles, projectId)) {
            return true;
        }
        else {
            return false;
        }
    }
    static hasPojectWriteRole(roles, projectId) {
        return Array.isArray(roles) && roles.includes(`p-${projectId}-write`);
    }
    static hasPojectReadRole(roles, projectId) {
        return Array.isArray(roles) && roles.includes(`p-${projectId}`);
    }
    static hasFeatureWriteRole(roles, projectId, feature) {
        return Array.isArray(roles) && (roles.includes(`p-${projectId}-${feature}-write`));
    }
    static hasFeatureIdWriteRole(roles, projectId, feature, featureId) {
        return Array.isArray(roles) && roles.includes(`p-${projectId}-${feature}-${featureId}-write`);
    }
    static hasFeatureReadRole(roles, projectId, feature) {
        return Array.isArray(roles) && roles.includes(`p-${projectId}-${feature}`);
    }
    static hasFeatureIdReadRole(roles, projectId, feature, featureId) {
        return Array.isArray(roles) && roles.includes(`p-${projectId}-${feature}-${featureId}`);
    }
    static canRead(roles, projectId, feature, featureId) {
        if (this.canWrite(roles, projectId, feature, featureId)) {
            return true;
        }
        else if (projectId && feature && featureId && this.hasFeatureIdReadRole(roles, projectId, feature, featureId)) {
            return true;
        }
        else if (projectId && feature && this.hasFeatureReadRole(roles, projectId, feature)) {
            return true;
        }
        else if (projectId && this.hasPojectReadRole(roles, projectId)) {
            return true;
        }
        else {
            return false;
        }
    }
}
